import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserInfo } from '../types'; // Assuming UserInfo type is defined
import { toast } from 'react-toastify';
import { LayoutDashboard, User, LogOut, Menu, X } from 'lucide-react'; // Added Menu and X icons

const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const userInfoString = localStorage.getItem('userInfo');
    const user: UserInfo | null = userInfoString ? JSON.parse(userInfoString) : null;

    const [isMenuOpen, setIsMenuOpen] = useState(false); // State for mobile menu visibility
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // State for logout confirmation modal

    // Function to handle the actual logout
    const performLogout = () => {
        localStorage.removeItem('userInfo');
        toast.info('Logged out successfully!');
        navigate('/login');
        setShowLogoutConfirm(false); // Close the modal
        setIsMenuOpen(false); // Close mobile menu if open
    };

    // Handler to open the logout confirmation modal
    const logoutHandler = () => {
        setShowLogoutConfirm(true);
    };

    return (
        <nav className="bg-blue-600 p-4 shadow-md sticky top-0 z-50">
            <div className="container mx-auto flex justify-between items-center">
                {/* Brand Logo */}
                <Link to="/" className="text-white text-2xl font-bold hover:text-blue-200 transition duration-300">
                    BetMania
                </Link>

                {/* Hamburger Icon for Mobile */}
                <div className="md:hidden">
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white focus:outline-none">
                        {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>

                {/* Desktop Navigation Links */}
                <div className="hidden md:flex items-center space-x-6">
                    {user ? (
                        <>
                            <Link to="/" className="text-white hover:text-blue-200 transition duration-300">
                                Home
                            </Link>
                            <Link to="/my-bets" className="text-white hover:text-blue-200 transition duration-300">
                                My Bets
                            </Link>

                            {user.isAdmin && (
                                <Link
                                    to="/admin"
                                    className="text-white hover:text-blue-200 transition duration-300 font-semibold flex items-center space-x-1"
                                >
                                    <LayoutDashboard size={20} />
                                    <span>Admin Dashboard</span>
                                </Link>
                            )}

                            <Link
                                to="/profile"
                                className="text-white hover:text-blue-200 transition duration-300 flex items-center space-x-1"
                            >
                                <User size={20} />
                                <span>Profile</span>
                            </Link>

                            <button
                                onClick={logoutHandler}
                                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-full transition duration-300 flex items-center space-x-1"
                            >
                                <LogOut size={20} />
                                <span>Logout</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-white hover:text-blue-200 transition duration-300">
                                Login
                            </Link>
                            <Link to="/register" className="text-white hover:text-blue-200 transition duration-300">
                                Register
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {isMenuOpen && (
                <div className="md:hidden bg-blue-700 py-4 mt-2 rounded-lg shadow-lg">
                    <div className="flex flex-col items-center space-y-4">
                        {user ? (
                            <>
                                <Link
                                    to="/"
                                    className="text-white hover:text-blue-200 transition duration-300 w-full text-center py-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Home
                                </Link>
                                <Link
                                    to="/my-bets"
                                    className="text-white hover:text-blue-200 transition duration-300 w-full text-center py-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    My Bets
                                </Link>

                                {user.isAdmin && (
                                    <Link
                                        to="/admin"
                                        className="text-white hover:text-blue-200 transition duration-300 font-semibold flex items-center justify-center space-x-1 w-full py-2"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        <LayoutDashboard size={20} />
                                        <span>Admin Dashboard</span>
                                    </Link>
                                )}

                                <Link
                                    to="/profile"
                                    className="text-white hover:text-blue-200 transition duration-300 flex items-center justify-center space-x-1 w-full py-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    <User size={20} />
                                    <span>Profile</span>
                                </Link>

                                <button
                                    onClick={logoutHandler}
                                    className="bg-blue-600 hover:bg-blue-800 text-white font-semibold py-2 px-4 rounded-full transition duration-300 flex items-center justify-center space-x-1 w-auto"
                                >
                                    <LogOut size={20} />
                                    <span>Logout</span>
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    className="text-white hover:text-blue-200 transition duration-300 w-full text-center py-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Login
                                </Link>
                                <Link
                                    to="/register"
                                    className="text-white hover:text-blue-200 transition duration-300 w-full text-center py-2"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Register
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirm Logout</h3>
                        <p className="text-gray-600 mb-6">Are you sure you want to log out?</p>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={performLogout}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full transition duration-300"
                            >
                                Yes, Logout
                            </button>
                            <button
                                onClick={() => setShowLogoutConfirm(false)}
                                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-full transition duration-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
